import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Plus, Search, Trash2, X } from 'lucide-react';
import type { Conversation } from '@diggens/types';
import { ICON_SIZES, ICON_STROKE } from './icons';
import { useConversations, useDeleteConversation } from './useConversations';
import { formatWhen } from './time';

interface ConversationListProps {
  /** The conversation on screen, which the list highlights; null for a new one. */
  currentConversationId: string | null;
  onSelect: (conversationId: string) => void;
  onNew: () => void;
}

const titleOf = (conversation: Conversation) => conversation.title || 'Untitled';

/** The Manager's past conversations beside the chat: start a new one, search them, open one, or delete one. */
export function ConversationList({ currentConversationId, onSelect, onNew }: ConversationListProps) {
  const { data: conversations, isLoading, error } = useConversations();
  const remove = useDeleteConversation();
  const [search, setSearch] = useState('');
  // The search field opens from its button, and closing it clears the search
  const [searching, setSearching] = useState(false);
  const closeSearch = () => {
    setSearching(false);
    setSearch('');
  };
  const [deleting, setDeleting] = useState<Conversation | null>(null);

  // Searches titles and messages, as the history drawer it replaces did
  const shown = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!conversations || !query) return conversations ?? [];
    return conversations.filter(
      (conversation) =>
        titleOf(conversation).toLowerCase().includes(query) ||
        JSON.stringify(conversation.history ?? [])
          .toLowerCase()
          .includes(query)
    );
  }, [conversations, search]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 2, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Conversations
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Search">
              <IconButton size="small" aria-label="Search" aria-pressed={searching} onClick={() => (searching ? closeSearch() : setSearching(true))}>
                <Search size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
              </IconButton>
            </Tooltip>
            <Button size="small" variant="outlined" startIcon={<Plus size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />} onClick={onNew} aria-label="New conversation">
              New
            </Button>
          </Box>
        </Box>
        {searching && (
          <TextField
            size="small"
            autoFocus
            placeholder="Search conversations"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') closeSearch();
            }}
            slotProps={{
              htmlInput: { 'aria-label': 'Search conversations' },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" edge="end" aria-label="Close search" onClick={closeSearch}>
                      <X size={ICON_SIZES.sm} strokeWidth={ICON_STROKE} />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mx: 2 }}>
            {error instanceof Error ? error.message : 'Could not load your conversations'}
          </Alert>
        )}
        {conversations && shown.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
            {search.trim() ? 'No conversations match.' : 'Your conversations with your Chief of Staff show up here.'}
          </Typography>
        )}
        <List dense disablePadding sx={{ px: 1 }}>
          {shown.map((conversation) => (
            <ListItem
              key={conversation.id}
              disablePadding
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  aria-label={`Delete ${titleOf(conversation)}`}
                  onClick={() => {
                    remove.reset();
                    setDeleting(conversation);
                  }}
                >
                  <Trash2 size={ICON_SIZES.md} strokeWidth={ICON_STROKE} />
                </IconButton>
              }
              // The delete button shows on hover or keyboard focus, and always on touch screens, which cannot hover
              sx={{
                '& .MuiListItemSecondaryAction-root': { opacity: 0 },
                '&:hover .MuiListItemSecondaryAction-root, &:focus-within .MuiListItemSecondaryAction-root': { opacity: 1 },
                '@media (hover: none)': { '& .MuiListItemSecondaryAction-root': { opacity: 1 } },
              }}
            >
              <ListItemButton selected={conversation.id === currentConversationId} onClick={() => onSelect(conversation.id)} sx={{ borderRadius: 1, pr: 5 }}>
                <ListItemText
                  primary={titleOf(conversation)}
                  secondary={formatWhen(conversation.updated_at)}
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {deleting && (
        <Dialog open onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Delete this conversation?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              “{titleOf(deleting)}” and its messages are deleted for good.
            </Typography>
            {remove.error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {remove.error.message}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleting(null)}>Cancel</Button>
            <Button
              color="error"
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(deleting.id, {
                  onSuccess: () => {
                    // The chat cannot keep showing a conversation that no longer exists
                    if (deleting.id === currentConversationId) onNew();
                    setDeleting(null);
                  },
                })
              }
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
